import React, { MouseEventHandler, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { fromJS } from 'immutable';
import { createSession } from 'flow/actions/chat-actions';
import {
    createPresenceNotification,
    deletePresenceNotification,
} from 'flow/actions/extension-actions';
import { createFavorite, deleteFavorite } from 'flow/actions/favorites-actions';
import CallTransferDialogWrapper from 'flow/components/call-transfer-dialog/CallTransferDialogWrapper';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectChatSessionByChatUser } from 'flow/selectors/chat-selectors';
import { getContact } from 'flow/selectors/contact-selectors';
import {
    selectOwnUserHasCapabilityVideo1on1,
    getExtension,
    canSetPresenceNotification,
    getChatUserKeyForExtensionWithKey,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { selectIsAdmin } from 'flow/selectors/own-extension-selectors';
import { getHasPresenceNotification } from 'flow/selectors/selectors';
import { ContactKey, ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import {
    isContactKey,
    isExtensionKey,
    isExtensionStatusInCall,
} from 'flow/utils';
import { addChatLogEntry } from 'flow/utils/browser-utils';
import {
    getFixedNumberFromContact,
    getMobileNumberFromContact,
    getTechnicalNumberFromContact,
} from 'flow/utils/extension-utils';
import { FavoriteType } from 'flow/utils/favorite-utils';
import CallButtonMenuItem from './CallButtonMenuItem';
import EmailFieldMenuItem from './EmailFieldMenuItem';
import VideoCallButtonMenuItem from './VideoCallButtonMenuItem';
import QuickTransferMenuItem from './QuickTransferMenuItem';
import { selectFavoriteFromEntityKey } from 'flow/selectors/favorite-selectors';

interface Props {
    entityKey: ExtensionKey | ContactKey;
}

const ContactContextMenu = ({ entityKey }: Props) => {
    const { i18next, showDialpad, setShowDialpad } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const ownUserHasVideo1on1Capability = useAppSelector(
        selectOwnUserHasCapabilityVideo1on1,
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { hasFeature } = useFeatureFlags();

    const extension = useAppSelector((state) =>
        isExtensionKey(entityKey) ? getExtension(state, entityKey) : undefined,
    );
    const isAiCoworker = extension?.get('extensionType') === 'ai_coworker';
    const isAdmin = useAppSelector(selectIsAdmin);

    const contact = useAppSelector((state) =>
        isContactKey(entityKey)
            ? getContact(state, entityKey as ContactKey)
            : extension?.get('contact'),
    );

    const favorite = useAppSelector((state) =>
        selectFavoriteFromEntityKey(entityKey, state.favorites),
    );

    const hasPresenceNotification = useAppSelector((state) =>
        getHasPresenceNotification(state, ownExtensionKey, entityKey),
    );

    const allowPresenceNotification = useAppSelector((state) =>
        canSetPresenceNotification(state, entityKey),
    );

    const thisExtensionIsUnavailable = useAppSelector((state) =>
        isExtensionStatusInCall(
            state.extensions.getIn(['statuses', entityKey]),
        ),
    );

    const chatUserKey = useAppSelector((state) =>
        getChatUserKeyForExtensionWithKey(state, entityKey),
    );

    const chatSessionKey = useAppSelector((state) =>
        selectChatSessionByChatUser(state, chatUserKey, ownExtensionKey),
    );

    const [transferSelectionDialogOpen, setTransferSelectionDialogOpen] =
        useState(false);

    const goToChat: MouseEventHandler = (event) => {
        event.stopPropagation();
        if (chatSessionKey) {
            history.push(
                generatePath(routes['messages.id'], { id: chatSessionKey }),
            );
        } else {
            try {
                dispatch(createSession(chatUserKey)).then((id: string) => {
                    history.push(
                        generatePath(routes['messages.id'], {
                            id,
                        }),
                    );
                });
            } catch (error) {
                addChatLogEntry('Failed to create chat session', error);
            }
        }
    };

    const toggleFavorite: MouseEventHandler = (event) => {
        if (!favorite) {
            if (isExtensionKey(entityKey)) {
                const favoriteDTO = fromJS({
                    type: FavoriteType.extension,
                    extension: extension,
                });
                dispatch(createFavorite(favoriteDTO));
            } else {
                const favoriteDTO = fromJS({
                    type: FavoriteType.contact,
                    contact,
                });
                dispatch(createFavorite(favoriteDTO));
            }
        } else {
            dispatch(deleteFavorite(favorite.get('key')));
        }

        event.stopPropagation();
    };

    const togglePresenceNotification: MouseEventHandler = (event) => {
        if (hasPresenceNotification) {
            dispatch(deletePresenceNotification(ownExtensionKey, extension));
        } else {
            dispatch(createPresenceNotification(ownExtensionKey, extension));
        }
        event.stopPropagation();
    };

    const showNotifyWhenAvailable = !!extension && allowPresenceNotification;
    const emailField = contact ? contact.get('email') : undefined;

    const mobileNumber = getMobileNumberFromContact(contact);
    const fixedNumber = getFixedNumberFromContact(contact);
    const technicalNumber = getTechnicalNumberFromContact(contact);

    const canMakeVideoCall =
        extension &&
        ownUserHasVideo1on1Capability &&
        hasFeature(FeatureFlags.VIDEO_CANVAS);

    const onClickSettingsOnBehalf = () => {
        if (showDialpad) {
            setShowDialpad(false);
        }

        history.push(
            generatePath(routes['settings'], {
                extensionKey: extension.get('key'),
            }),
        );
    };

    return (
        <>
            {!transferSelectionDialogOpen && (
                <>
                    <CallButtonMenuItem
                        fixedNumber={fixedNumber}
                        technicalNumber={technicalNumber}
                        mobileNumber={mobileNumber}
                        extensionToCall={extension}
                    />

                    {canMakeVideoCall && !isAiCoworker && (
                        <VideoCallButtonMenuItem
                            extensionKey={extension.get('key')}
                        />
                    )}

                    <QuickTransferMenuItem
                        openTransferSelectionDialog={() =>
                            setTransferSelectionDialogOpen(true)
                        }
                        fixedNumber={fixedNumber}
                        mobileNumber={mobileNumber}
                        transferContact={contact}
                        handleClose={() => {}}
                    />

                    {!!extension && !isAiCoworker && (
                        <MenuItem onClick={goToChat}>
                            {i18next.t('Tooltip.Title.SendMessage')}
                        </MenuItem>
                    )}

                    {isAdmin && !isAiCoworker && extension?.get('key') ? (
                        <>
                            <Divider className="my-2" />
                            <MenuItem onClick={onClickSettingsOnBehalf}>
                                {i18next.t(
                                    'settings_on_behalf.edit_user_settings',
                                )}
                            </MenuItem>
                        </>
                    ) : null}

                    {emailField || showNotifyWhenAvailable || extension ? (
                        <Divider className="my-2" />
                    ) : null}

                    <MenuItem onClick={toggleFavorite}>
                        {favorite
                            ? i18next.t('General.Favorite.Remove.Favorite')
                            : i18next.t('General.Favorite.Add.Favorite')}
                    </MenuItem>

                    <EmailFieldMenuItem emailField={emailField} />

                    {showNotifyWhenAvailable && (
                        <MenuItem onClick={togglePresenceNotification}>
                            <div>
                                {i18next.t(
                                    'General.ExtensionBusyAlet.NotifyWhenAvailable',
                                )}
                            </div>
                            {hasPresenceNotification ? (
                                <Icon iconKey="done" className="me-2" />
                            ) : null}
                        </MenuItem>
                    )}
                </>
            )}
            {transferSelectionDialogOpen && (
                <CallTransferDialogWrapper
                    phoneNumber={fixedNumber}
                    contact={contact}
                    transferContact={contact}
                    handleClose={() => setTransferSelectionDialogOpen(false)}
                    thisExtensionIsUnavailable={thisExtensionIsUnavailable}
                    logLocation={'quick transfer context menu'}
                />
            )}
        </>
    );
};

export default React.memo(ContactContextMenu);
